// @ts-nocheck
import { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, MessageSquare, Phone,
  MailOpen, MousePointerClick, CalendarCheck, PhoneIncoming,
  PhoneCall, RefreshCw, UserPlus, FileText, Tag, Trash2, Zap, Plus, X,
  Clock, Calendar, ClipboardList, ClipboardCheck, ListChecks,
} from 'lucide-react';
import { useRecallSequenceStore } from '../../recalls/sequenceBuilderStore';
import type { SequenceNode, BlockType, ConditionMode, BlockProperties } from '../../recalls/sequenceBuilderStore';
import { useV1Scope } from './SequenceV1Scope';

const ICON_MAP: Record<string, React.ReactNode> = {
  'trigger-new-contact':          <UserPlus size={16} />,
  'trigger-appointment-booked':   <CalendarCheck size={16} />,
  'trigger-form-submitted':       <FileText size={16} />,
  'trigger-tag-added':            <Tag size={16} />,
  'action-send-email':            <Mail size={24} />,
  'action-send-sms':              <MessageSquare size={24} />,
  'action-ai-voice-call':         <Phone size={24} />,
  'action-send-survey':           <ClipboardList size={24} />,
  'condition-email-opened':       <MailOpen size={16} />,
  'condition-link-clicked':       <MousePointerClick size={16} />,
  'condition-appointment-booked': <CalendarCheck size={16} />,
  'condition-call-received':      <PhoneIncoming size={16} />,
  'condition-ai-call-answered':   <PhoneCall size={16} />,
  'condition-followup-requested': <RefreshCw size={16} />,
  'condition-survey-submitted':   <ClipboardCheck size={16} />,
  'condition-survey-answered':    <ListChecks size={16} />,
};

interface BlockNodeProps {
  node: SequenceNode;
}

// ─── Timer Addon Badge ────────────────────────────────────────────────────────
// Shown above action/condition blocks when a timer is set.
// Collapsed = small amber circle with clock icon.
// Expands on hover or when the parent block is selected to show the label.

interface TimerAddonProps {
  node: SequenceNode;
  /** If true, the timer cannot be turned off (first condition after action) */
  required?: boolean;
  /** Whether the parent block is currently selected/hovered */
  expanded?: boolean;
}

function getTimerLabel(props: BlockProperties): string {
  const mode = props.timerMode ?? 'wait';
  if (mode === 'schedule') {
    if (!props.scheduledDate) return 'Scheduled';
    const dateStr = new Date(props.scheduledDate).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
    const timeStr = props.scheduledTime ? ` at ${props.scheduledTime}` : '';
    return `${dateStr}${timeStr}`;
  }
  if (props.delayAmount) {
    return `Wait ${props.delayAmount} ${props.delayUnit ?? 'days'}`;
  }
  return 'Set delay';
}

function TimerAddon({ node, required = false, expanded = false }: TimerAddonProps) {
  const [hovered, setHovered] = useState(false);
  const isExpanded = expanded || hovered;

  const timerIcon = (node.properties.timerMode ?? 'wait') === 'schedule'
    ? <Calendar size={16} />
    : <Clock size={16} />;

  return (
    <motion.div
      data-id={`timer-addon-${node.id}`}
      className="flex flex-col items-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <motion.div
        layout
        animate={{ width: isExpanded ? 'auto' : 28 }}
        transition={{ duration: 0.22, ease: 'easeInOut' }}
        className="flex items-center gap-1.5 h-7 px-1.5  rounded-full bg-elva-ink-2 text-elva-surface overflow-hidden"
        style={{ minWidth: 28 }}
      >
        <span className="shrink-0 text-amber-400">{timerIcon}</span>
        <AnimatePresence>
          {isExpanded && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.18 }}
              className="text-[10px] font-medium whitespace-nowrap overflow-hidden"
            >
              {getTimerLabel(node.properties)}
            </motion.span>
          )}
        </AnimatePresence>
        {isExpanded && required && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="shrink-0 text-elva-surface/40 text-[9px]"
          >
            req
          </motion.span>
        )}
      </motion.div>
      {/* Connector line between timer badge and block */}
      <div style={{ width: 2, height: 8, background: 'var(--elva-ink-3)' }} />
    </motion.div>
  );
}

// ─── Condition block ──────────────────────────────────────────────────────────

interface ConditionBlockNodeProps {
  node: SequenceNode;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  isFirstAfterAction: boolean;
}

function ConditionBlockNode({ node, isSelected, onClick, isFirstAfterAction }: ConditionBlockNodeProps) {
  const [hovered, setHovered] = useState(false);
  const getAncestorTypes = useRecallSequenceStore((s) => s.getAncestorTypes);
  const openBottomSheet = useRecallSequenceStore((s) => s.openBottomSheet);
  const removeAndCondition = useRecallSequenceStore((s) => s.removeAndCondition);
  const { andCondition, checkConditionMode } = useV1Scope();

  const ancestors = getAncestorTypes(node.id);
  const theme = resolveConditionTheme(ancestors);
  const andConditions = node.properties.andConditions ?? [];
  const hasAnd = andConditions.length > 0;
  // V1 (§8.4.7 row 2): force Branch display when Check is disabled.
  const rawConditionMode: ConditionMode = node.properties.conditionMode ?? 'branching';
  const conditionMode: ConditionMode = checkConditionMode ? rawConditionMode : 'branching';
  const isCheckMode = conditionMode === 'check';

  const hasTimer = (node.properties.delayAmount !== undefined || node.properties.scheduledDate !== undefined);

  const handleAddCondition = (e: React.MouseEvent) => {
    e.stopPropagation();
    openBottomSheet('condition-picker', {
      nodeId: node.id,
      nodeLabel: node.label,
      ancestorTypes: ancestors,
    });
  };

  const handleRemoveAnd = (e: React.MouseEvent, type: string) => {
    e.stopPropagation();
    removeAndCondition(node.id, type as never);
  };

  return (
    <div
      data-block="true"
      data-id={`block-node-${node.id}`}
      className="relative flex flex-col items-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Timer addon above condition */}
      {hasTimer && (
        <TimerAddon
          node={node}
          required={isFirstAfterAction}
          expanded={isSelected || hovered}
        />
      )}

      {/* AND plus button — slides down from behind the pill (hidden in V1: no AND-chaining, §8.4.7 row 3) */}
      <AnimatePresence>
        {andCondition && hovered && !isCheckMode && (
          <motion.button
            data-id={`condition-add-btn-${node.id}`}
            data-add-btn="true"
            initial={{ y: -8, opacity: 0, scaleX: 0.7 }}
            animate={{ y: 0, opacity: 1, scaleX: 1 }}
            exit={{ y: -8, opacity: 0, scaleX: 0.7 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onClick={handleAddCondition}
            className={`
              absolute -bottom-9 z-0
              flex items-center gap-1 h-8 w-8 px-2.5
              rounded-full ${theme.bg} text-elva-surface text-[10px] font-medium
              shadow-md hover:brightness-110 transition-all
            `}
            style={{ originY: 0 }}
          >
            <Plus size={36} strokeWidth={2.5} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Condition pill */}
      <motion.div
        layout
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onClick={onClick}
        className="cursor-pointer relative z-10"
      >
        <div className={`
          flex flex-col items-center py-1.5
          ${theme.bg} text-elva-surface
          shadow-elva-sm transition-all overflow-hidden
          ${isSelected ? `ring-2 ring-offset-2 ring-offset-elva-bg ${theme.ring} shadow-lg` : 'hover:shadow-md'}
          ${(hasAnd || isSelected) ? 'rounded-xl' : 'rounded-full'}
        `}>
          {/* Primary condition row */}
          <div className="flex items-center justify-start py-1 w-full px-4 gap-2">
            <span className="text-sm font-semibold truncate flex-1">{node.label}</span>
          </div>

          {/* AND-stacked conditions */}
          <AnimatePresence>
            {andConditions.map((cond, i) => (
              <motion.div
                key={cond.type}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="w-full overflow-hidden"
              >
                <div className="flex items-center gap-2 px-4 py-1">
                  <span className="text-xs font-medium flex-1 truncate">& {cond.label}</span>
                  {isSelected && (
                    <button
                      data-id={`remove-and-${node.id}-${i}`}
                      onClick={(e) => handleRemoveAnd(e, cond.type)}
                      className="w-4 h-4 rounded-full bg-elva-surface/20 hover:bg-elva-surface/40 flex items-center justify-center transition-colors shrink-0"
                    >
                      <X size={8} />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

        </div>
      </motion.div>
    </div>
  );
}

const ACTION_THEME = {
  email:   { bg: 'bg-elva-channel-email', ring: 'ring-elva-channel-email', hex: '#3A6EA5' },
  sms:     { bg: 'bg-elva-iris-plum', ring: 'ring-elva-iris-plum', hex: '#8E3567' },
  call:    { bg: 'bg-elva-green', ring: 'ring-elva-green', hex: '#0F5C45' },
  survey:  { bg: 'bg-elva-iris-coral', ring: 'ring-elva-iris-coral', hex: '#D8625F' },
  default: { bg: 'bg-elva-green-deep',  ring: 'ring-elva-green-deep',   hex: '#0A4530' },
} as const;

type ActionThemeKey = keyof typeof ACTION_THEME;

function resolveConditionTheme(ancestorTypes: string[]): (typeof ACTION_THEME)[ActionThemeKey] {
  for (let i = ancestorTypes.length - 1; i >= 0; i--) {
    const t = ancestorTypes[i];
    if (t === 'action-send-email') return ACTION_THEME.email;
    if (t === 'action-send-sms') return ACTION_THEME.sms;
    if (t === 'action-ai-voice-call') return ACTION_THEME.call;
    if (t === 'action-send-survey') return ACTION_THEME.survey;
  }
  return ACTION_THEME.default;
}

export const SequenceBlockNode = memo(function SequenceBlockNode({ node }: BlockNodeProps) {
  const selectedBlockId = useRecallSequenceStore((s) => s.selectedBlockId);
  const highlightedNodeId = useRecallSequenceStore((s) => s.highlightedNodeId);
  const selectBlock = useRecallSequenceStore((s) => s.selectBlock);
  const deleteNode = useRecallSequenceStore((s) => s.deleteNode);
  const openSidebar = useRecallSequenceStore((s) => s.openSidebar);
  const clearEditChat = useRecallSequenceStore((s) => s.clearEditChat);
  const getAncestorTypes = useRecallSequenceStore((s) => s.getAncestorTypes);
  const { entryNodeEditable } = useV1Scope();

  const isSelected = selectedBlockId === node.id;
  const isHighlighted = highlightedNodeId === node.id;

  // V1 (§8.4.7 row 7): the entry/trigger node is display-only — bound to the
  // delivery-mode/trigger from the audience stage, not authored in the builder.
  const triggerEditable = node.category !== 'trigger' || entryNodeEditable;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!triggerEditable) return;
    selectBlock(node.id);
    clearEditChat();
    openSidebar('edit');
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNode(node.id);
  };

  const subtitle = getSubtitle(node);

  // ── Condition block ───────────────────────────────────────────────────────
  if (node.category === 'condition') {
    const ancestors = getAncestorTypes(node.id);
    const immediateParentType = ancestors[ancestors.length - 1];
    const ACTION_TYPES: BlockType[] = ['action-send-email', 'action-send-sms', 'action-ai-voice-call', 'action-send-survey'];
    const isFirstAfterAction = !!immediateParentType && ACTION_TYPES.includes(immediateParentType);

    return (
      <ConditionBlockNode
        node={node}
        isSelected={isSelected}
        onClick={handleClick}
        isFirstAfterAction={isFirstAfterAction}
      />
    );
  }

  // ── Trigger block ─────────────────────────────────────────────────────────
  if (node.category === 'trigger') {
    return (
      <motion.div
        data-block="true"
        data-id={`block-node-${node.id}`}
        whileHover={triggerEditable ? { scale: 1.02, y: -1 } : {}}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onClick={handleClick}
        className={triggerEditable ? 'cursor-pointer' : 'cursor-default'}
      >
        <div className={`
          flex items-center gap-3 px-4 h-[72px] min-w-[320px]
          rounded-lg bg-elva-surface dark:bg-zinc-900
          border-2 border-red-400 dark:border-red-800
          transition-all shadow-lg
          ${isSelected ? 'ring-2 ring-offset-2 ring-offset-elva-bg ring-elva-negative shadow-lg' : 'hover:shadow-md'}
        `}>
          <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center shrink-0 text-red-500">
            <Zap size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-elva-ink-1 truncate leading-tight">{node.label}</p>
            {subtitle && <p className="text-xs text-elva-ink-3 truncate mt-0.5">{subtitle}</p>}
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Action block ──────────────────────────────────────────────────────────
  const hasTimer = node.properties.delayAmount !== undefined || node.properties.scheduledDate !== undefined;

  const BgColorMap: Record<string, string> = {
    'action-send-email':    'bg-elva-channel-email-soft text-elva-channel-email',
    'action-send-sms':      'bg-elva-iris-plum-soft text-elva-iris-plum',
    'action-ai-voice-call': 'bg-elva-green-soft text-elva-green',
    'action-send-survey':   'bg-elva-iris-coral-soft text-elva-iris-coral',
  };
  const iconColorMap: Record<string, string> = {
    'action-send-email':    'text-elva-channel-email',
    'action-send-sms':      'text-elva-iris-plum',
    'action-ai-voice-call': 'text-elva-green',
    'action-send-survey':   'text-elva-iris-coral',
  };
  const highlightRingMap: Record<string, string> = {
    'action-send-email':    'ring-elva-channel-email',
    'action-send-sms':      'ring-elva-iris-plum',
    'action-ai-voice-call': 'ring-elva-green',
    'action-send-survey':   'ring-elva-iris-coral',
  };

  const iconCls = iconColorMap[node.type] ?? 'text-elva-channel-email';
  const highlightRing = highlightRingMap[node.type] ?? 'ring-elva-green';

  return (
    <div className="relative flex flex-col items-center" data-block="true" data-id={`block-node-${node.id}`}>
      {/* Timer addon above action — only shown when timer is configured */}
      {hasTimer && (
        <TimerAddon node={node} required={false} expanded={isSelected} />
      )}

      {isHighlighted && (
        <motion.div
          className={`absolute inset-0 rounded-md ring-2 ${highlightRing} pointer-events-none`}
          animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.04, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      <motion.div
        whileHover={{ scale: 1.02, y: -1 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onClick={handleClick}
        className="cursor-pointer"
      >
        <div className={`
          flex items-center gap-3 px-3 h-[72px] min-w-[320px]
          rounded-md transition-all
          ${BgColorMap[node.type] ?? 'bg-elva-channel-email-soft'}
          ${isSelected ? `ring-2 ring-offset-2 ring-offset-elva-bg ${highlightRing} shadow-lg` : 'hover:shadow-md'}
        `}>
          <div className={`w-12 h-12 bg-elva-surface rounded-lg flex items-center justify-center shrink-0 ${iconCls}`}>
            {ICON_MAP[node.type] ?? <Mail size={16} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 leading-tight">
              <p className="text-sm font-bold text-elva-ink-1 truncate">{node.label}</p>
              {node.properties.blockTag && (
                <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-elva-surface/60 dark:bg-black/30 text-elva-ink-1/70 border border-elva-border-soft/50 truncate max-w-[80px]">
                  {node.properties.blockTag}
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-elva-ink-3 truncate mt-0.5">{subtitle}</p>}
          </div>
          {isSelected && (
            <motion.button
              data-id={`delete-block-${node.id}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={handleDelete}
              className="w-6 h-6 rounded-lg flex items-center justify-center
                text-elva-ink-3 hover:text-destructive hover:bg-destructive/10
                transition-colors shrink-0"
            >
              <Trash2 size={12} />
            </motion.button>
          )}
        </div>
      </motion.div>
    </div>
  );
});

function getSubtitle(node: SequenceNode): string | null {
  switch (node.type) {
    case 'action-send-email':
      return node.properties.emailTemplateId
        ? (node.properties.subject ?? 'Email selected')
        : 'Select email template';
    case 'action-send-sms':
      return node.properties.message
        ? node.properties.message.slice(0, 30) + '…'
        : 'Write your message';
    case 'action-ai-voice-call':
      return node.properties.callTemplateId ? 'Call template selected' : 'Select call template';
    case 'action-send-survey':
      return node.properties.surveyTemplateId ? 'Survey selected' : 'Select survey form';
    default:
      return null;
  }
}
